/**
 * The MCs a W-FMCSA sample run handled, by what became of each, to audit them downstream: take an MC from here and look it
 * up on the consumer side. Only a sample keeps the list; a whole run handles the whole register.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import type { BrokerPipelineRunMcs } from '@numeo/types-core'
import * as React from 'react'
import { formatNumber } from '../../load-pipeline/components/shared'

const OUTCOMES: { key: keyof BrokerPipelineRunMcs; label: string; hint: string }[] = [
    { key: 'state', label: 'STATE published', hint: 'their facts differed from the last published, so a STATE went out' },
    { key: 'unchanged', label: 'Unchanged', hint: 'the same facts as the last published: nothing went out' },
    { key: 'refused', label: 'Refused', hint: 'publish refused the update; its reason is in the log' },
    { key: 'failed', label: 'Failed', hint: 'a read or a publish did not go through; the next run states them again' },
    { key: 'dat', label: 'DAT_DIRECTORY published', hint: 'their offices and profile from the DAT Directory went out' },
]

function CopyButton({ mcs, label }: { mcs: string[]; label: string }) {
    const [copied, setCopied] = React.useState(false)
    function copy(): void {
        navigator.clipboard
            .writeText(mcs.join('\n'))
            .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
            })
            .catch(() => undefined)
    }
    return (
        <Button size="sm" variant="plain" disabled={mcs.length === 0} onClick={copy}>
            {copied ? 'Copied' : label}
        </Button>
    )
}

export function FmcsaRunMcs({ mcs, dryRun }: { mcs: BrokerPipelineRunMcs; dryRun: boolean }) {
    const all = [...new Set([...mcs.state, ...mcs.unchanged, ...mcs.refused, ...mcs.failed])]
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography level="title-sm">The sample&apos;s MCs ({formatNumber(all.length)})</Typography>
                <CopyButton mcs={all} label="Copy all" />
            </Box>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                {dryRun ? 'A dry run: what would have gone out, and nothing did. ' : ''}One MC per line when copied, to look each up downstream.
            </Typography>
            {OUTCOMES.filter(({ key }) => mcs[key].length > 0).map(({ key, label, hint }) => (
                <Box key={key} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography level="body-sm" fontWeight="lg">
                            {label} ({formatNumber(mcs[key].length)})
                        </Typography>
                        <CopyButton mcs={mcs[key]} label="Copy" />
                    </Box>
                    <Typography level="body-xs" textColor="text.tertiary">
                        {hint}
                    </Typography>
                    <Typography
                        level="body-xs"
                        fontFamily="code"
                        sx={{ mt: 0.5, maxHeight: 96, overflowY: 'auto', wordBreak: 'break-word', bgcolor: 'background.level1', borderRadius: 'sm', p: 1 }}
                    >
                        {mcs[key].join(', ')}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}
